import {PACKAGING_METHOD} from '../../../../../../../config/constants';
import {capitalizeFirst, numberWithSpaces} from '../../../../../../../utils/helpers';
import i18n from '../../../../../../../config/i18';

const isTariffAmount = (value?: number) => typeof value === 'number' && Number.isFinite(value);

const hasCombinedCargoPrice = (term: FreightTerm) => (
  [ term.price_kg, term.price_m3, term.min_cost ].some(isTariffAmount)
);

export const getTariffTerms = ({freights, summary}: DeliveryTariffTermsParams): DeliveryTariffTerms[] => (
  freights.flatMap((freight) => {
    const deliveryType = summary?.delivery_type ?? freight.order?.delivery_type;
    const isContainerDelivery = deliveryType === PACKAGING_METHOD.CONTAINERS;
    const isCombinedCargoDelivery = deliveryType === PACKAGING_METHOD.COMBINED_CARGO;

    if (!isContainerDelivery && !isCombinedCargoDelivery) return [];

    const containers = summary?.containers_user ?? freight.order?.containers_map ?? freight.containers_map;
    const terms = (freight.terms || []).filter((term) => {
      const containerType = term.container_type?.trim().toLowerCase();

      if (isCombinedCargoDelivery) return !containerType && hasCombinedCargoPrice(term);

      if (!containerType) return false;

      const containerCount = containers?.[`type_${containerType}`] ?? 0;

      return containerCount > 0;
    });

    if (!terms.length) return [];

    return [ {
      freightId: freight.id,
      locationFrom: freight.location_from.name,
      locationTo: freight.location_to.name,
      terms,
    } ];
  })
);

const formatConditionClause = (clause: string): FormattedFreightConditionClause | undefined => {
  const minimumContainers = clause.match(/^container_count\s*>=\s*(\d+)$/);

  if (minimumContainers) {
    return {
      variable: 'container_count',
      text: i18n.t('calc__text__delivery__minimumContainers', {count: Number(minimumContainers[1])}),
      continuation: i18n.t('calc__text__delivery__minimumContainersContinuation', {
        value: numberWithSpaces(minimumContainers[1]),
      }),
    };
  }

  const comparison = clause.match(/^(gross_weight_total|volume_total)\s*(>=|<=|==|!=|>|<)\s*(\d+(?:\.\d+)?)$/);

  if (!comparison) return;

  const signs: Record<string, string> = {
    '>=': '≥',
    '<=': '≤',
    '==': '=',
    '!=': '≠',
    '>': '>',
    '<': '<',
  };
  const translationKey = comparison[1] === 'gross_weight_total'
    ? 'calc__text__delivery__grossWeightCondition'
    : 'calc__text__delivery__volumeCondition';

  const translationValues = {
    sign: signs[comparison[2]],
    value: numberWithSpaces(comparison[3]),
  };

  return {
    variable: comparison[1],
    text: i18n.t(translationKey, translationValues),
    continuation: i18n.t(`${translationKey}Continuation`, translationValues),
  };
};

const formatConditions = (conditions?: string) => {
  const expression = conditions?.trim();

  if (!expression) return '';

  const clauses = expression.split(/\s+and\s+/).map((clause) => formatConditionClause(clause.trim()));

  if (clauses.some((clause) => !clause)) {
    return i18n.t('calc__text__delivery__additionalTariffConditions');
  }

  const conditionTexts = clauses.map((clause, index) => {
    if (!clause) return '';

    const repeatsPreviousVariable = clause.variable === clauses[index - 1]?.variable;

    return repeatsPreviousVariable ? clause.continuation : clause.text;
  });

  return conditionTexts.join(` ${i18n.t('calc__text__delivery__conditionAnd')} `);
};

const formatCombinedCargoPrice = (term: FreightTerm) => {
  const {currency} = term;
  const prices = [
    isTariffAmount(term.price_kg)
      ? i18n.t('calc__text__delivery__pricePerKg', {price: numberWithSpaces(term.price_kg), currency})
      : '',
    isTariffAmount(term.price_m3)
      ? i18n.t('calc__text__delivery__pricePerCubicMeter', {price: numberWithSpaces(term.price_m3), currency})
      : '',
    isTariffAmount(term.min_cost)
      ? i18n.t('calc__text__delivery__minimumCost', {price: numberWithSpaces(term.min_cost), currency})
      : '',
    isTariffAmount(term.volume_weight)
      ? i18n.t('calc__text__delivery__volumeWeight', {value: numberWithSpaces(term.volume_weight)})
      : '',
  ];

  return prices.filter(Boolean).join('; ');
};

export const formatTerm = (term: FreightTerm) => {
  const containerType = term.container_type?.trim();
  const conditionText = formatConditions(term.conditions);

  if (!containerType) {
    if (!hasCombinedCargoPrice(term)) return '';

    const price = formatCombinedCargoPrice(term);
    const text = conditionText ? `${conditionText} — ${price}` : price;

    return capitalizeFirst(text, i18n.resolvedLanguage);
  }

  if (!isTariffAmount(term.price)) return '';

  const condition = conditionText ? ` (${conditionText})` : '';
  const price = `${numberWithSpaces(term.price)} ${term.currency}`;

  return `${containerType.toUpperCase()}${condition} — ${price}`;
}

export const formatTerms = (terms: FreightTerm[]) => terms.map(formatTerm).filter(Boolean).join('; ');
